import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { fetchMicrosoftSettings } from "services/company.service";

const WARN_DAYS_BEFORE_EXPIRY = 30;
// Fired by the Microsoft Connect settings form after a save: the layout (and this banner) stays
// mounted around the settings page, so without it the banner would show the old date until a reload.
export const MICROSOFT_SETTINGS_SAVED_EVENT = "microsoft-settings-saved";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole calendar days from today to `expiresAt` ('YYYY-MM-DD'); negative once past.
 * Both days are compared as UTC-midnight calendar dates: `new Date('2028-10-13')` is
 * UTC midnight, which would be the previous evening (a day off) in Vilnius.
 */
export function daysUntil(expiresAt: string, today: Date = new Date()): number {
  const [year, month, day] = expiresAt.split("-").map(Number);
  const expiryDay = Date.UTC(year, month - 1, day);
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((expiryDay - todayDay) / MS_PER_DAY);
}

/**
 * Reminds the company's Masters that the Azure client secret behind OneDrive is about to
 * expire (or already has: documents and files stop working). Hidden when no date is set.
 */
export function MicrosoftSecretExpiryBanner({ companyId }: { companyId: string }) {
  const [secretExpiresAt, setSecretExpiresAt] = useState<string | null>(null);

  useEffect(() => {
    let isCurrent = true;
    const loadExpiryDate = () => {
      fetchMicrosoftSettings(companyId)
        .then((settings) => {
          if (isCurrent) setSecretExpiresAt(settings.secretExpiresAt ?? null);
        })
        .catch(() => {
          // A failed check only hides the banner.
          if (isCurrent) setSecretExpiresAt(null);
        });
    };

    setSecretExpiresAt(null);
    loadExpiryDate();
    window.addEventListener(MICROSOFT_SETTINGS_SAVED_EVENT, loadExpiryDate);
    return () => {
      isCurrent = false;
      window.removeEventListener(MICROSOFT_SETTINGS_SAVED_EVENT, loadExpiryDate);
    };
  }, [companyId]);

  if (!secretExpiresAt) return null;
  const daysLeft = daysUntil(secretExpiresAt);
  if (daysLeft > WARN_DAYS_BEFORE_EXPIRY) return null;

  const isExpired = daysLeft < 0;
  const colors = isExpired
    ? "border-red-200 bg-red-50 text-red-800"
    : "border-amber-200 bg-amber-50 text-amber-900";

  return (
    <div role="alert" className={`mx-4 mt-4 flex items-start gap-3 rounded-lg border px-4 py-3 max-md:mx-2 max-md:mt-2 max-md:gap-2 max-md:px-3 max-md:py-2 ${colors}`}>
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <p className="text-sm">
        <Trans
          i18nKey={isExpired ? "microsoft_secret_expired_banner" : "microsoft_secret_expiring_banner"}
          values={{ date: secretExpiresAt, days: daysLeft }}
          count={daysLeft}
          components={{
            settingsLink: (
              <Link to={`/app/${companyId}/settings/storage`} className="font-semibold underline" />
            ),
          }}
        />
      </p>
    </div>
  );
}
